import {
  ChargeSessionCodeProvenance,
  ChargeSessionCodeType,
  InvoiceTypes,
  SugggestedCodeType
} from '../../../enums';
import { isEqual, get, unionBy } from '../../object-utils';
import {
  extractCodeDescriptionForCode,
  extractShortDescriptionForCode
} from './dictionary/code-description-dictionary-utils';

type Option = {
  label: string;
  value: string;
  highlight?: boolean;
};

/**
 * Method to get default department
 * if there's only one existing department, returns that department
 * otherwise returns empty department
 * @param invoiceOptions
 * @returns
 */
export const getDefaultDepartment = (
  invoiceOptions: AmplifyAPI.InvoiceOptionsDTO
): AmplifyAPI.Department => {
  const existingDepartments =
    invoiceOptions?.summary?.existing_departments ?? [];
  const departmentCode =
    existingDepartments?.length === 1 ? existingDepartments[0]?.code : null;
  if (departmentCode) {
    return getDepartmentFromCode(invoiceOptions, departmentCode);
  }
  return { code: null, name: null };
};

/**
 * Method to return department object for a given department code
 * @param invoiceOptions
 * @param departmentCode
 * @returns
 */
export const getDepartmentFromCode = (
  invoiceOptions: AmplifyAPI.InvoiceOptionsDTO,
  departmentCode: string
): AmplifyAPI.Department => {
  const allDepartments = getAllDepartments(invoiceOptions);
  let department: AmplifyAPI.Department;
  if (departmentCode) {
    department = allDepartments.find(item => item.code === departmentCode);
  }
  return department ?? { code: null, name: null };
};

/**
 * Method to return all departments as union of all_departments and all_departments
 * In case of invoice with old deprtament data existing_departments has departments that are not present in all_departments
 * @param invoiceOptions
 * @returns
 */
export const getAllDepartments = (
  invoiceOptions: AmplifyAPI.InvoiceOptionsDTO
): AmplifyAPI.Department[] => {
  const allDepartments = invoiceOptions?.summary?.all_departments ?? [];
  const existingDepartments =
    invoiceOptions?.summary?.existing_departments ?? [];
  const combinedAllDepartments = unionBy(
    [...allDepartments, ...existingDepartments],
    department => department.code
  );
  return combinedAllDepartments;
};

/**
 * Method to return  department options
 * In case of invoice with old deprtament data existing_departments has department with name= null, this is prepared for UI purpose to have department name and code as department code
 * @param departments
 * @returns
 */
export const getPreparedDepartmentOptions = (
  departments: AmplifyAPI.Department[],
  existingDepartments: Set<string>
): Option[] => {
  const updatedDepartments = departments.map(item => {
    return {
      value: item.code,
      label: item.name ?? item.code,
      highlight: existingDepartments?.has(item.code) ?? false
    };
  });
  return updatedDepartments;
};

/** Function to compare properties of two objects */
export const compareObjectProperties = (
  obj1: { [key: string]: any },
  obj2: { [key: string]: any },
  propertiesToCompare: string[]
): boolean => {
  for (const prop of propertiesToCompare) {
    if (!isEqual(get(obj1, prop), get(obj2, prop))) {
      return false;
    }
  }
  return true;
};

/** Function to check if a field has value upon a given string is present in list */
export const isFieldValid = (
  listObj: string[],
  compareValue: string,
  field: any,
  defaultVal: boolean = true
) => {
  return listObj?.includes(compareValue)
    ? field !== null && field !== undefined && field !== ''
    : defaultVal;
};

function procedureCodeToCQATaskCode(
  procedure: AmplifyAPI.ProcedureCode
): CMxCommonApp.CQATaskCode {
  return {
    code: procedure.procedure_code,
    codeType: ChargeSessionCodeType.PROCEDURE,
    description: '',
    codeCorrection: null,
    assessment: null,
    role: null,
    units: procedure.units,
    editable: false,
    sequence_number: procedure.sequence_number
  };
}

function modifierCodeToCQATaskCode(
  modifier: AmplifyAPI.ModifierCode
): CMxCommonApp.CQATaskCode {
  return {
    code: modifier.modifier_code,
    codeType: ChargeSessionCodeType.MODIFIER,
    description: '',
    codeCorrection: null,
    assessment: null,
    role: null,
    editable: false,
    sequence_number: modifier.sequence_number
  };
}

function diagnosisCodeToCQATaskCode(
  diagnosis: AmplifyAPI.DiagnosisCode
): CMxCommonApp.CQATaskCode {
  return {
    code: diagnosis.diagnosis_code,
    codeType: ChargeSessionCodeType.DIAGNOSIS,
    description: '',
    codeCorrection: null,
    assessment: null,
    role: null,
    editable: false,
    sequence_number: diagnosis.sequence_number
  };
}

//Takes an invoice used in amplify and converts it to a gradeable invoice for CQA
export function translateInvoiceToGradeableInvoice(
  invoice: AmplifyAPI.Invoice | CMxCommonApp.CodableInvoice
): CMxCommonApp.GradeableInvoice {
  const { transactions, ...rest } = invoice;
  const gradeableTransactions: CMxCommonApp.GradeableTransaction[] = transactions.map(
    transaction => {
      return {
        ...transaction,
        codes: [
          ...(transaction.procedure
            ? [procedureCodeToCQATaskCode(transaction.procedure)]
            : []),
          ...transaction.diagnoses.map(diagnosisCodeToCQATaskCode),
          ...transaction.modifiers.map(modifierCodeToCQATaskCode)
        ]
      };
    }
  );

  // Return the new GradeableInvoice with transformed transactions
  return {
    ...rest,
    transactions: gradeableTransactions,
    errorMessages: []
  };
}

const createCodeProvenanceForInvoice = (
  invoice: AmplifyAPI.Invoice,
  transaction: AmplifyAPI.Transaction
): CMxCommonApp.CodeProvenance | null => {
  if (invoice.type.toLowerCase() == InvoiceTypes.CLINICAL.toLowerCase()) {
    return {
      artifact_type: InvoiceTypes.CLINICAL,
      source_id: transaction.guid,
      source_id_type: 'transaction.guid'
    };
  }

  if (invoice.type.toLowerCase() == InvoiceTypes.PREDICTION.toLowerCase()) {
    return {
      artifact_type: InvoiceTypes.PREDICTION,
      // Invoices from the predicted artifiact do not have transactions, the invoice guid is the artifact guid
      source_id: invoice.guid,
      source_id_type: 'artifact.guid'
    };
  }

  if (invoice.type.toLowerCase() == InvoiceTypes.ASSEMBLED.toLowerCase()) {
    return {
      artifact_type: InvoiceTypes.ASSEMBLED,
      source_id: transaction.guid,
      source_id_type: 'transaction.guid'
    };
  }

  if (invoice.type.toLowerCase() == InvoiceTypes.MAPPED.toLowerCase()) {
    return {
      artifact_type: InvoiceTypes.MAPPED,
      source_id: transaction.guid,
      source_id_type: 'transaction.guid'
    };
  }
};
// Ensure that code provenances are uniformly ordered across suggested codes
const orderCodeProvenance = (codeProvenance: CMxCommonApp.CodeProvenance[]) => {
  return [...codeProvenance].sort((a, b) => {
    return InvoiceTypes[a.artifact_type].localeCompare(
      InvoiceTypes[b.artifact_type]
    );
  });
};

//Take existing code provenance for the code and optionally add the code provenance for the source invoice, sort it
const getUpdatedCodeProvenance = (
  baseProvenance: CMxCommonApp.CodeProvenance[] | null,
  additionalProvenance: CMxCommonApp.CodeProvenance | null
): CMxCommonApp.CodeProvenance[] => {
  const newProvenance = [...(baseProvenance ?? [])];
  if (additionalProvenance) {
    newProvenance.push(additionalProvenance);
  }
  return orderCodeProvenance(newProvenance);
};

/**
 * Transforms source invoices into separate lists of CPT, ICD, and modifier codes
 * @param sourceInvoices Array of source invoices to process
 * @returns Object containing separate lists for CPT, ICD, and modifier codes
 *
 *
 * transaction_guids is not relevent to CodeSourcesList
 * code_source and code_recent_date are not relevent to CodeSourcesList
 */
export const sourceInvoicesToCodeSourceList = (
  sourceInvoices: AmplifyAPI.Invoice[],
  codeDescriptions: CMxAPI.CodeResponse
): {
  cptSourceList: CMxCommonApp.SuggestedLinkedProcedure[];
  icdSourceList: CMxCommonApp.SuggestedCode[];
  modSourceList: CMxCommonApp.SuggestedCode[];
} => {
  const cptSourceList: CMxCommonApp.SuggestedLinkedProcedure[] = [];
  const icdSourceList: CMxCommonApp.SuggestedCode[] = [];
  const modSourceList: CMxCommonApp.SuggestedCode[] = [];

  sourceInvoices.forEach(sourceInvoice => {
    sourceInvoice.transactions.forEach(transaction => {
      //Add a code provenance for the source invoice the code currently belongs to
      const additionalCodeProvenance = createCodeProvenanceForInvoice(
        sourceInvoice,
        transaction
      );
      // Process procedures (CPT codes)
      if (transaction.procedure) {
        // Check if this CPT + modifiers combination already exists
        const existingCptIndex = cptSourceList.findIndex(item => {
          const itemModifierCodes = item.modifiers
            .map(mod => mod.code)
            .sort()
            .join(',');
          const transactionModifierCodes = transaction.modifiers
            .map(mod => mod.modifier_code)
            .sort()
            .join(',');
          return (
            item.code === transaction.procedure.procedure_code &&
            itemModifierCodes === transactionModifierCodes
          );
        });

        //Take existing code provenance for the code and optionally add the code provenance for the source invoice
        const codeProvenance = getUpdatedCodeProvenance(
          transaction.procedure.code_provenances,
          additionalCodeProvenance
        );

        if (existingCptIndex === -1) {
          cptSourceList.push({
            code: transaction.procedure.procedure_code,
            codeType: ChargeSessionCodeProvenance.CPT,
            description: extractCodeDescriptionForCode(
              codeDescriptions[transaction.procedure.procedure_code]
            ),
            units: transaction.procedure.units || 1,
            shortDescription: extractShortDescriptionForCode(
              codeDescriptions[transaction.procedure.procedure_code]
            ),
            effectiveDate: '',
            tenantDefined: true,
            code_source: '',
            code_recent_date: '',
            suggested_code_type: SugggestedCodeType.FROM_SOURCES,
            transaction_guids: [],
            codeProvenances: codeProvenance,
            modifiers: transaction.modifiers.map(mod => ({
              code: mod.modifier_code,
              codeType: ChargeSessionCodeProvenance.MODIFIER,
              effectiveDate: '',
              description: extractCodeDescriptionForCode(
                codeDescriptions[mod.modifier_code]
              ),
              shortDescription: extractShortDescriptionForCode(
                codeDescriptions[mod.modifier_code]
              ),
              tenantDefined: true,
              code_source: '',
              code_recent_date: '',
              suggested_code_type: SugggestedCodeType.FROM_SOURCES,
              transaction_guids: [],
              codeProvenances: codeProvenance
            }))
          });
        } else {
          //If there is a match we want to copy code provenances to this existing suggested code
          const updatedCodeProvenance = orderCodeProvenance([
            ...(cptSourceList[existingCptIndex].codeProvenances ?? []),
            ...codeProvenance
          ]);

          const existingCpt = cptSourceList[existingCptIndex];
          existingCpt.codeProvenances = updatedCodeProvenance;
          existingCpt.modifiers = existingCpt.modifiers.map(mod => ({
            ...mod,
            codeProvenances: codeProvenance
          }));
        }
      }

      // Process diagnoses (ICD codes)
      transaction.diagnoses.forEach(diagnosis => {
        //Take existing code provenance for the code and optionally add the code provenance for the source invoice
        const codeProvenance = getUpdatedCodeProvenance(
          diagnosis.code_provenances,
          additionalCodeProvenance
        );

        const existingIcdIndex = icdSourceList.findIndex(
          item => item.code === diagnosis.diagnosis_code
        );
        if (existingIcdIndex === -1) {
          icdSourceList.push({
            code: diagnosis.diagnosis_code,
            codeType: ChargeSessionCodeProvenance.ICD,
            description: extractCodeDescriptionForCode(
              codeDescriptions[diagnosis.diagnosis_code]
            ),
            shortDescription: extractShortDescriptionForCode(
              codeDescriptions[diagnosis.diagnosis_code]
            ),
            effectiveDate: '',
            tenantDefined: true,
            code_source: '',
            code_recent_date: '',
            suggested_code_type: SugggestedCodeType.FROM_SOURCES,
            transaction_guids: [],
            codeProvenances: codeProvenance
          });
        } else {
          //If there is a match we want to copy code provenances to this existing suggested code
          const updatedCodeProvenance = orderCodeProvenance([
            ...(icdSourceList[existingIcdIndex].codeProvenances ?? []),
            ...codeProvenance
          ]);
          icdSourceList[
            existingIcdIndex
          ].codeProvenances = updatedCodeProvenance;
        }
      });

      // Process modifiers
      transaction.modifiers.forEach(modifier => {
        const existingModIndex = modSourceList.findIndex(
          item => item.code === modifier.modifier_code
        );

        const codeProvenance = getUpdatedCodeProvenance(
          modifier.code_provenances,
          additionalCodeProvenance
        );

        if (existingModIndex === -1) {
          modSourceList.push({
            code: modifier.modifier_code,
            codeType: ChargeSessionCodeType.MODIFIER,
            description: extractCodeDescriptionForCode(
              codeDescriptions[modifier.modifier_code]
            ),
            shortDescription: extractShortDescriptionForCode(
              codeDescriptions[modifier.modifier_code]
            ),
            effectiveDate: '',
            tenantDefined: true,
            code_source: '',
            code_recent_date: '',
            suggested_code_type: SugggestedCodeType.FROM_SOURCES,
            transaction_guids: [],
            codeProvenances: codeProvenance
          });
        } else {
          //If there is a match we want to copy code provenances to this existing suggested code
          const updatedCodeProvenance = orderCodeProvenance([
            ...(modSourceList[existingModIndex].codeProvenances ?? []),
            ...codeProvenance
          ]);
          modSourceList[
            existingModIndex
          ].codeProvenances = updatedCodeProvenance;
        }
      });
    });
  });

  return {
    cptSourceList,
    icdSourceList,
    modSourceList
  };
};

export const providerOrder = (providers) => {
  return providers?.sort((a, b) => {
    return a?.displayName?.toUpperCase()
        .localeCompare(b?.displayName?.toUpperCase())
  })
}
